import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../common/components/functional/dialogs/BasicDialog";
import {
  CloseConversationOptions,
  ClosingMessageLanguage,
  DEFAULT_CLOSE_CONVERSATION_OPTIONS,
  getConversationClosingMessage,
} from "./conversationClosingMessage";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (options: CloseConversationOptions) => void;
};

const ResolveConversationDialog = ({ open, onClose, onConfirm }: Props) => {
  const { t } = useTranslation(["messaging", "common"]);
  const [sendClosingMessage, setSendClosingMessage] = useState(
    DEFAULT_CLOSE_CONVERSATION_OPTIONS.sendClosingMessage,
  );
  const [language, setLanguage] = useState<ClosingMessageLanguage>(
    DEFAULT_CLOSE_CONVERSATION_OPTIONS.language,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) return;
    setSendClosingMessage(
      DEFAULT_CLOSE_CONVERSATION_OPTIONS.sendClosingMessage,
    );
    setLanguage(DEFAULT_CLOSE_CONVERSATION_OPTIONS.language);
    setIsSubmitting(false);
  }, [open]);

  const handleConfirm = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    onConfirm({ sendClosingMessage, language });
  };

  return (
    <BasicDialog
      open={open}
      title={t("messaging:resolveConversationTitle")}
      onCancel={onClose}
      onConfirm={handleConfirm}
      confirmDisabled={isSubmitting}
      confirmLabel={t("messaging:markAsDone")}
      variant="white"
      PaperProps={{ sx: { width: "560px" } }}
      content={
        <Stack spacing={2}>
          <FormControlLabel
            sx={{ alignSelf: "flex-start" }}
            control={
              <Checkbox
                checked={sendClosingMessage}
                onChange={(_, checked) => setSendClosingMessage(checked)}
                sx={{ ml: -1.5 }}
              />
            }
            label={t("messaging:sendClosingMessage")}
          />
          <FormControl disabled={!sendClosingMessage}>
            <FormLabel id="closing-message-language-label">
              {t("messaging:closingMessageLanguage")}
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="closing-message-language-label"
              name="closing-message-language"
              value={language}
              onChange={(event) =>
                setLanguage(event.target.value as ClosingMessageLanguage)
              }
            >
              <FormControlLabel
                value="fi"
                control={<Radio />}
                label={t("common:fi")}
              />
              <FormControlLabel
                value="en"
                control={<Radio />}
                label={t("common:en")}
              />
            </RadioGroup>
          </FormControl>
          {sendClosingMessage && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {getConversationClosingMessage(language)}
            </Typography>
          )}
        </Stack>
      }
    />
  );
};

export default ResolveConversationDialog;
